/**
 * Enhanced Gift Form - JavaScript Controller
 * Handles typeform-style navigation and interactions
 * Updated v2.1 - Added surprise box skip logic and improved navigation
 */

class GiftForm {
  constructor(container) {
    this.container = container;
    this.currentStep = 1; // Start at step 1 (size selector), skip launch page
    this.totalSteps = container.querySelectorAll('.typeform-step').length;
    this.selectedData = {
      product: null,
      categories: [], // Changed from single category to array
      subcategories: [],
      categoryTextInputs: {}, // Store category-specific text inputs
      surpriseBox: false, // Surprise box toggle
      questions: {
        occasion: '',
        allergies: '',
        color_palette: [],
        quirky_answer: '',
        recipient_count: '',
        surprise_box: '',
        custom_note: '',
        delivery_date: ''
      },
      customQuestions: {},
      step4Data: {
        textarea: '',
        date: ''
      }
    };

    // Product selection is always enabled
    this.hasProductSelection = true;

    // Check if animations are enabled
    this.animationsEnabled = container.dataset.animations === 'true';

    // Initialize product metafields storage
    this.productMetafields = {};

    this.init();
  }

  init() {
    if (new URLSearchParams(window.location.search).get('restart') === '1') {
      sessionStorage.removeItem('giftBoxProgress');
    }
    this.loadDummyData();
    this.loadMetaobjectData();
    this.bindEvents();
    this.loadProducts();
    this.updateProgressIndicator();
    this.initDateInputs();
    this.filterHolidayOccasions();

    // Initialize date preview if we're on step 5
    if (this.currentStep === 5) {
      const dateInput = this.container.querySelector('#step4-date');
      if (dateInput) {
        this.updateDatePreview(dateInput);
      }
    }
  }

  initDateInputs() {
    // Min date = tomorrow, preselected = 3 weekdays from now
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = this.formatDateISO(tomorrow);

    const defaultDate = new Date();
    let weekdaysAdded = 0;
    while (weekdaysAdded < 3) {
      defaultDate.setDate(defaultDate.getDate() + 1);
      const day = defaultDate.getDay();
      if (day !== 0 && day !== 6) weekdaysAdded++;
    }
    const preselectedDate = this.formatDateISO(defaultDate);

    const dateInputs = this.container.querySelectorAll('input[type="date"]');
    dateInputs.forEach(input => {
      input.setAttribute('min', minDate);
      if (!input.value) {
        input.value = preselectedDate;
        this.updateDatePreview(input);
      }
    });
  }

  formatDateISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  enforceDateMin(input) {
    // Some mobile native date pickers let users select dates before the `min`
    // attribute. Snap the value forward to `min` if that happens.
    if (!input || !input.value) return;
    const min = input.getAttribute('min');
    if (min && input.value < min) {
      input.value = min;
    }
  }

  filterHolidayOccasions() {
    const occasionSelect = this.container.querySelector('[data-property-name="Occasion"]');
    if (!occasionSelect) return;

    const now = new Date();
    const year = now.getFullYear();

    // Holiday occasions with their approximate dates
    // Mother's Day NZ = 2nd Sunday of May, Father's Day NZ = 1st Sunday of September
    const holidays = {
      'Christmas': new Date(year, 11, 25),
      "Mother's Day": this.getNthSundayOfMonth(year, 4, 2),  // May, 2nd Sunday
      "Father's Day": this.getNthSundayOfMonth(year, 8, 1),  // September, 1st Sunday
    };

    // If the holiday has passed this year, use next year's date
    for (const [name, date] of Object.entries(holidays)) {
      if (date < now) {
        if (name === 'Christmas') {
          holidays[name] = new Date(year + 1, 11, 25);
        } else if (name === "Mother's Day") {
          holidays[name] = this.getNthSundayOfMonth(year + 1, 4, 2);
        } else if (name === "Father's Day") {
          holidays[name] = this.getNthSundayOfMonth(year + 1, 8, 1);
        }
      }
    }

    const twoMonthsMs = 60 * 24 * 60 * 60 * 1000;
    const options = Array.from(occasionSelect.querySelectorAll('option'));
    const placeholder = options[0]; // "Select an option..."

    const holidayOptions = [];
    const regularOptions = [];

    options.forEach(option => {
      if (option === placeholder) return;
      const val = option.value;
      if (holidays[val]) {
        const diff = holidays[val] - now;
        // Only show if within 2 months before the holiday
        if (diff >= 0 && diff <= twoMonthsMs) {
          holidayOptions.push(option);
        } else {
          option.remove();
        }
      } else {
        regularOptions.push(option);
      }
    });

    // Rebuild: placeholder, then holiday options at top, then regular options
    occasionSelect.innerHTML = '';
    occasionSelect.appendChild(placeholder);
    holidayOptions.forEach(opt => occasionSelect.appendChild(opt));
    regularOptions.forEach(opt => occasionSelect.appendChild(opt));
  }

  getNthSundayOfMonth(year, month, n) {
    const date = new Date(year, month, 1);
    let count = 0;
    while (count < n) {
      if (date.getDay() === 0) count++;
      if (count < n) date.setDate(date.getDate() + 1);
    }
    return date;
  }

  saveProgress() {
    const state = {
      currentStep: this.currentStep,
      selectedData: this.selectedData,
      step4Textarea: this.container.querySelector('#step4-textarea')?.value || '',
      step4Date: this.container.querySelector('#step4-date')?.value || '',
      customQuestionValues: {}
    };

    // Save custom question values from the DOM
    this.container.querySelectorAll('[data-property-name]').forEach(input => {
      const key = input.dataset.propertyName;
      if (input.tagName === 'SELECT') {
        state.customQuestionValues[key] = input.value;
      } else if (input.type === 'checkbox') {
        if (!state.customQuestionValues[key]) state.customQuestionValues[key] = [];
        if (input.checked) state.customQuestionValues[key].push(input.value);
      } else if (input.type === 'date') {
        state.customQuestionValues[key] = input.value;
      } else if (input.tagName === 'TEXTAREA') {
        state.customQuestionValues[key] = input.value;
      }
    });

    sessionStorage.setItem('giftBoxProgress', JSON.stringify(state));
  }

  restoreProgress() {
    const saved = sessionStorage.getItem('giftBoxProgress');
    if (!saved) return false;

    try {
      const state = JSON.parse(saved);
      if (!state.selectedData || !state.currentStep) return false;

      // Restore selected data
      this.selectedData = state.selectedData;

      // Restore product selection in DOM
      if (this.selectedData.product) {
        const productCard = this.container.querySelector(`[data-product-id="${this.selectedData.product.id}"]`);
        if (productCard) {
          productCard.classList.add('selected');
        }
      }

      // Restore category selections in DOM
      if (this.selectedData.categories?.length) {
        this.selectedData.categories.forEach(cat => {
          const catCard = this.container.querySelector(`[data-category-id="${cat.id}"]`);
          if (catCard) catCard.classList.add('selected');
        });
      }

      // Restore surprise box toggle
      if (this.selectedData.surpriseBox) {
        const surpriseToggle = this.container.querySelector('[data-surprise-box-toggle]');
        if (surpriseToggle) surpriseToggle.checked = true;
      }

      // Restore step 4/5 form fields
      const textarea = this.container.querySelector('#step4-textarea');
      if (textarea && state.step4Textarea) textarea.value = state.step4Textarea;

      const dateInput = this.container.querySelector('#step4-date');
      if (dateInput && state.step4Date) dateInput.value = state.step4Date;

      // Restore custom question values
      if (state.customQuestionValues) {
        Object.entries(state.customQuestionValues).forEach(([key, value]) => {
          const inputs = this.container.querySelectorAll(`[data-property-name="${key}"]`);
          inputs.forEach(input => {
            if (input.tagName === 'SELECT') {
              input.value = value;
            } else if (input.type === 'checkbox') {
              input.checked = Array.isArray(value) && value.includes(input.value);
            } else if (input.type === 'date') {
              input.value = value;
            } else if (input.tagName === 'TEXTAREA') {
              input.value = value;
            }
          });
        });
      }

      // Navigate to the saved step
      this.goToStep(state.currentStep);

      return true;
    } catch (e) {
      sessionStorage.removeItem('giftBoxProgress');
      return false;
    }
  }

  loadDummyData() {
    // No dummy data - using only metobject data

    // No dummy products data - using only collection data
  }

  loadMetaobjectData() {
    // Load metobject data from the JSON script tag
    const metaobjectDataScript = this.container.querySelector('[data-metaobject-data]');
    if (metaobjectDataScript) {
      try {
        const metaobjectData = JSON.parse(metaobjectDataScript.textContent);
        this.realCategories = metaobjectData.categories;
      } catch (error) {
        console.error('Failed to parse metobject data:', error);
        console.error('Script content:', metaobjectDataScript.textContent);
      }
    } else {
      console.warn('No metaobject data script found. Categories may not be available.');
    }

    // Load product metafields data from the JSON script tag
    const productMetafieldsScript = this.container.querySelector('[data-product-metafields]');
    if (productMetafieldsScript) {
      try {
        const metafieldsData = JSON.parse(productMetafieldsScript.textContent);
        this.productMetafields = {};
        if (metafieldsData.products) {
          metafieldsData.products.forEach(product => {
            this.productMetafields[product.id] = product.number_of_items;
            this.productMetafields[product.handle] = product.number_of_items; // Also index by handle
          });
        }
        console.log('Loaded product metafields:', this.productMetafields);
      } catch (error) {
        console.error('Failed to parse product metafields data:', error);
        this.productMetafields = {};
      }
    } else {
      console.warn('No product metafields script found. Metafields may not be available.');
      this.productMetafields = {};
    }
  }

  bindEvents() {
    // Navigation buttons
    this.container.addEventListener('click', (e) => {
      if (e.target.matches('[data-next-step]')) {
        e.preventDefault();
        this.nextStep();
      } else if (e.target.matches('[data-prev-step]')) {
        e.preventDefault();
        this.prevStep();
      } else if (e.target.matches('[data-add-to-cart]')) {
        e.preventDefault();
        this.addToCart();
      }
    });

    // Card selections
    this.container.addEventListener('click', (e) => {
      const option = e.target.closest('.typeform-option');
      if (option) {
        this.handleCardSelection(option);
      }
    });

    // Handle form submission for add to cart
    this.container.addEventListener('submit', (e) => {
      if (e.target.matches('#gift-product-form')) {
        // Let the form submit naturally to Shopify
      }
    });

    // Edit buttons in summary
    this.container.addEventListener('click', (e) => {
      if (e.target.matches('.summary-edit')) {
        const step = parseInt(e.target.dataset.editStep);
        this.goToStep(step);
      }
    });

    // Question form handling
    this.container.addEventListener('change', (e) => {
      const questionElement = e.target.closest('[data-question]');
      if (questionElement) {
        this.handleQuestionChange(e.target);
      }
    });

    this.container.addEventListener('input', (e) => {
      const questionElement = e.target.closest('[data-question]');
      if (questionElement) {
        this.handleQuestionChange(e.target);
      }
    });

    // Custom questions handling
    this.container.addEventListener('change', (e) => {
      if (e.target.matches('.question-input, .question-select, .question-checkbox')) {
        this.handleCustomQuestionChange(e.target);
        // Update navigation state when field changes
        this.updateNavigationState();
      }
    });

    this.container.addEventListener('input', (e) => {
      if (e.target.matches('.question-input')) {
        this.handleCustomQuestionChange(e.target);
        // Update navigation state when field changes
        this.updateNavigationState();
      } else if (e.target.matches('[data-category-text-input]')) {
        this.handleCategoryTextInputChange(e.target);
      } else if (e.target.matches('#step4-date') || e.target.matches('.question-date')) {
        this.enforceDateMin(e.target);
        this.updateDatePreview(e.target);
        // Update placeholder state
        this.updateDatePlaceholder(e.target);
        // Update navigation state when date changes
        this.updateNavigationState();
      }
    });

    // Handle date change for mobile preview
    this.container.addEventListener('change', (e) => {
      if (e.target.matches('#step4-date') || e.target.matches('.question-date')) {
        this.enforceDateMin(e.target);
        this.updateDatePreview(e.target);
        // Update placeholder state
        this.updateDatePlaceholder(e.target);
        // Update navigation state when date changes
        this.updateNavigationState();
      }
    });

    // Surprise box toggle
    this.container.addEventListener('change', (e) => {
      if (e.target.matches('[data-surprise-box-toggle]')) {
        this.selectedData.surpriseBox = e.target.checked;
        console.log('Surprise box toggled:', this.selectedData.surpriseBox);

        // If surprise box is checked, unselect all categories
        if (this.selectedData.surpriseBox) {
          this.selectedData.categories = [];
          // Remove selected class from all category cards
          this.container.querySelectorAll('[data-category-id]').forEach(card => {
            card.classList.remove('selected');
          });
        }

        this.updateNavigationState();
      }
    });

  }

  loadProducts() {
    const productContainer = this.container.querySelector('[data-product-selection]');
    if (!productContainer) return;

    const collectionHandle = this.container.dataset.collection;

    if (collectionHandle && collectionHandle.trim() !== '' && collectionHandle !== 'undefined') {
      // Fetch products from the actual collection
      this.fetchCollectionProducts(collectionHandle, productContainer);
    } else {
      // Fallback to dummy data for preview
      this.loadDummyProducts(productContainer);
    }
  }

  fetchCollectionProducts(collectionHandle, container) {
    // Show loading state
    container.innerHTML = `
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading products from collection...</p>
      </div>
    `;

    // Use Shopify's Collection API
    fetch(`/collections/${collectionHandle}/products.json?limit=12`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.products && data.products.length > 0) {
          // Use metafields from script tag (loaded in loadMetaobjectData)
          const metafieldMap = {};
          data.products.forEach(product => {
            // Try to get metafield by product ID or handle
            const numberOfItems = this.productMetafields?.[product.id.toString()] ||
                                 this.productMetafields?.[product.handle] ||
                                 null;
            metafieldMap[product.id] = numberOfItems;
            metafieldMap[product.handle] = numberOfItems;
          });

          // Store the real products for selection handling
          this.realProducts = data.products.map(product => {
            const numberOfItems = metafieldMap[product.id] || metafieldMap[product.handle] || null;
            return {
              id: product.id.toString(),
              name: product.title,
              image: product.images[0]?.src || 'https://via.placeholder.com/400x400?text=No+Image',
              price: product.variants[0].price,
              variantId: product.variants[0].id,
              description: product.body_html ? this.stripHtml(product.body_html) : 'No description available',
              number_of_items: numberOfItems
            };
          });

          container.innerHTML = data.products.map(product => {
            const description = product.body_html ? this.truncateText(this.stripHtml(product.body_html), 120) : '';
            const numberOfItems = metafieldMap[product.id] || metafieldMap[product.handle] || null;

            return `
            <div class="typeform-option" data-product-id="${product.id}" data-variant-id="${product.variants[0].id}" tabindex="0" role="button" aria-label="Select ${product.title}">
              <img src="${product.images[0]?.src || 'https://via.placeholder.com/400x400?text=No+Image'}" alt="${product.title}" class="option-image" loading="lazy">
              <div class="option-content">
                <div class="option-title h3">${product.title}</div>
              </div>
              <div class="option-price font-base">${this.formatPrice(product.variants[0].price)} | ${numberOfItems ? numberOfItems : ''}</div>
              ${description ? `<p class="option-description text-base">${description}</p>` : ''}
            </div>
          `;
          }).join('');
          // Restore saved progress, or check for pre-selected size from URL parameter
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('size')) {
            this.autoSelectFromUrl();
          } else {
            this.restoreProgress();
          }
        } else {
          this.loadDummyProducts(container);
        }
      })
      .catch(error => {
        console.warn('Failed to load collection products:', error);
        this.loadDummyProducts(container);
      });
  }

  autoSelectFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const sizeParam = params.get('size');
    if (!sizeParam) return;

    const sizeName = sizeParam.toLowerCase().replace(/-/g, ' ');
    const productCards = this.container.querySelectorAll('[data-product-id]');

    for (const card of productCards) {
      const title = card.querySelector('.option-title');
      if (!title) continue;
      const cardTitle = title.textContent.trim().toLowerCase();
      // Match exactly or if product title starts with the size name (e.g. "Little Gift Box" matches "little")
      if (cardTitle === sizeName || cardTitle.startsWith(sizeName)) {
        // Select the card visually and in data
        this.container.querySelectorAll('[data-product-id]').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        const productId = card.dataset.productId;
        const productList = this.realProducts || this.products;
        this.selectedData.product = productList.find(p => p.id === productId || p.id === productId.toString());
        this.updateNavigationState();
        break;
      }
    }
  }

  loadDummyProducts(container) {
    setTimeout(() => {
      container.innerHTML = this.products.map((product, index) => `
        <div class="typeform-option" data-product-id="${product.id}" data-variant-id="${product.id}" tabindex="0" role="button" aria-label="Select ${product.name}">
          <img src="${product.image}" alt="${product.name}" class="option-image" loading="lazy">
          <div class="option-content">
            <div class="option-title h3">${product.name}</div>
          </div>
          <div class="option-price">${product.price}</div>
        </div>
      `).join('');
    }, 800);
  }



  loadSubcategories() {
    const subcategoryContainer = this.container.querySelector('[data-subcategory-selection]');
    if (!subcategoryContainer) return;

    if (this.selectedData.categories.length === 0) {
      subcategoryContainer.innerHTML = `
        <div class="loading-state">
          <p>Please select categories first to see available subcategories.</p>
        </div>
      `;
      return;
    }

    let html = '';

    // Group subcategories by category
    this.selectedData.categories.forEach(category => {
      const categoryData = this.realCategories?.find(c => c.id === category.id);
      if (categoryData && categoryData.subcategories && categoryData.subcategories.length > 0) {
        // Add category section header
        html += `
          <div class="category-section">
            <h3 class="category-section-title">${categoryData.name}</h3>
            <div class="category-subcategories">
        `;

        // Add subcategories for this category
        categoryData.subcategories.forEach((subcategory, index) => {
          const uniqueId = `sub_${categoryData.id}_${index}`;
          html += `
            <div class="typeform-option multi-select" data-subcategory-id="${uniqueId}" data-subcategory-name="${subcategory.name}" data-category-id="${categoryData.id}" tabindex="0" role="checkbox" aria-label="Select ${subcategory.name}">
              <div class="option-content">
                <div class="option-title">${subcategory.name}</div>
              </div>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }
    });

    if (html === '') {
      subcategoryContainer.innerHTML = `
        <div class="loading-state">
          <p>No subcategories available for the selected categories.</p>
        </div>
      `;
      return;
    }

    subcategoryContainer.innerHTML = html;
    this.restoreSubcategories();
    this.restoreCategoryTextInputs();
  }

  // Pull subcategories back through when revisiting
  restoreSubcategories() {
    if (!this.selectedData.subcategories?.length) return;

    // Get all available subcategory names from currently selected categories
    const availableSubcategoryNames = new Set();
    this.selectedData.categories.forEach(category => {
      const categoryData = this.realCategories?.find(c => c.id === category.id);
      if (categoryData && categoryData.subcategories) {
        categoryData.subcategories.forEach(sub => {
          availableSubcategoryNames.add(sub.name);
        });
      }
    });

    // Restore subcategories by name (not ID, since IDs are regenerated)
    this.selectedData.subcategories.forEach(subcategory => {
      // Only restore if this subcategory belongs to a currently selected category
      if (availableSubcategoryNames.has(subcategory.name)) {
        const card = this.container.querySelector(
          `[data-subcategory-name="${subcategory.name}"]`
        );

        if (card) {
          card.classList.add('selected');
        }
      }
    });
  }

  // Pull subcategory text back through when revisiting
  restoreCategoryTextInputs() {
  // loop over all text inputs
  const inputs = this.container.querySelectorAll('[data-category-text-input]');

  inputs.forEach(input => {
    const categoryId = input.dataset.categoryId;
    const saved = this.selectedData.categoryTextInputs?.[categoryId];

    if (saved) {
      input.value = saved.text || '';
    }
  });
  }

  getCategoryPlaceholder(categoryName) {
    // Define placeholder examples for different categories
    const placeholders = {
      'Pets and Animals': 'example.. My sister has a large Golden Retriever.',
      'Electronics': 'They love gaming and need a new headset',
      'Home & Garden': 'They just moved into a new apartment',
      'Fashion': 'They prefer minimalist style in neutral colors',
      'Sports': 'They play tennis every weekend',
      'Books': 'They enjoy mystery novels and biographies',
      'Beauty': 'They have sensitive skin and prefer natural products',
      'Food & Drink': 'They love trying new cuisines and cooking',
      'Food and Drink': 'example.. We love to host a cocktail night.',
      'Film and Music': 'example.. Miley Cyrus is my dad\'s favourite artist.',
      'Fitness and Outdoors': 'example.. My best friend loves yoga!',
      'Toys and Puzzles': 'example.. My nephew does a weekly crossword.',
      'Home and Living': 'example.. Green is my wife\'s favourite colour.',
      'Tools and Automotive': 'example.. My partner is always tinkering with cars.',
      'Gaming and Technology': 'example.. My brother is a tech expert.',
      'Health and Beauty': 'example.. My friend\'s nails are always perfect.',
      'Arts and Creativity': 'example.. My mum loves to write poems.',
      'Books and Education': 'example.. My colleague loves romance novels.',
      'Adventure and Travel': 'example.. My boyfriend and I are training for a marathon.'
    };

    return placeholders[categoryName] || 'Tell us more about your special someone...';
  }

  handleCardSelection(card) {
    const step = this.currentStep;

    if (step === 1 && card.dataset.productId) {
      // Product selection (single select)
      this.container.querySelectorAll('[data-product-id]').forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');

      // Use real products if available, otherwise fall back to dummy products
      const productList = this.realProducts || this.products;
      this.selectedData.product = productList.find(p => p.id === card.dataset.productId);

      // On mobile, auto-advance to next step after selecting a size
      if (window.innerWidth <= 768) {
        setTimeout(() => this.nextStep(), 300);
      }

    } else if (step === 2 && card.dataset.categoryId) {
      // Category selection (multi-select)
      card.classList.toggle('selected');
      const categoryId = card.dataset.categoryId;

      // Use real categories from metobject data
      const category = this.realCategories?.find(c => c.id === categoryId);

      if (card.classList.contains('selected')) {
        // Add category to selection
        if (category && !this.selectedData.categories.find(c => c.id === categoryId)) {
          this.selectedData.categories.push(category);

          // If surprise box is checked, uncheck it when categories are selected
          if (this.selectedData.surpriseBox) {
            this.selectedData.surpriseBox = false;
            const surpriseBoxCheckbox = this.container.querySelector('[data-surprise-box-toggle]');
            if (surpriseBoxCheckbox) {
              surpriseBoxCheckbox.checked = false;
            }
          }
        }
      } else {
        // Remove category from selection
        this.selectedData.categories = this.selectedData.categories.filter(c => c.id !== categoryId);

        // Remove subcategories that belong to the removed category
        const removedCategory = this.realCategories?.find(c => c.id === categoryId);
        if (removedCategory && removedCategory.subcategories) {
          const removedSubcategoryNames = removedCategory.subcategories.map(s => s.name);
          this.selectedData.subcategories = this.selectedData.subcategories.filter(
            s => !removedSubcategoryNames.includes(s.name)
          );
        }
      }

      // Reload subcategories based on all selected categories (preserving existing selections)
      this.loadSubcategories();
      this.restoreSubcategories();
      this.restoreCategoryTextInputs();

    } else if (step === 3 && card.dataset.subcategoryId) {
      // Subcategory selection (multi-select)
      card.classList.toggle('selected');
      const subcategoryId = card.dataset.subcategoryId;
      const subcategoryName = card.dataset.subcategoryName || card.querySelector('.option-title').textContent;

      const subcategory = { id: subcategoryId, name: subcategoryName };

      if (card.classList.contains('selected')) {
        // Check if subcategory is not already selected
        if (!this.selectedData.subcategories.find(s => s.name === subcategoryName)) {
          this.selectedData.subcategories.push(subcategory);
        }
      } else {
        this.selectedData.subcategories = this.selectedData.subcategories.filter(s => s.name !== subcategoryName);
      }
    }

    this.updateNavigationState();
  }

  handleQuestionChange(input) {
    const questionType = input.dataset.question;

    switch (questionType) {
      case 'occasion':
        this.selectedData.questions.occasion = input.value;
        break;

      case 'allergies':
        this.selectedData.questions.allergies = input.value;
        break;

      case 'color_palette':
        if (input.type === 'checkbox') {
          if (input.checked) {
            if (!this.selectedData.questions.color_palette.includes(input.value)) {
              this.selectedData.questions.color_palette.push(input.value);
            }
          } else {
            this.selectedData.questions.color_palette = this.selectedData.questions.color_palette.filter(
              value => value !== input.value
            );
          }
        }
        break;

      case 'quirky_answer':
        this.selectedData.questions.quirky_answer = input.value;
        break;

      case 'recipient_count':
        this.selectedData.questions.recipient_count = input.value;
        break;

      case 'surprise_box':
        this.selectedData.questions.surprise_box = input.value;
        break;

      case 'custom_note':
        this.selectedData.questions.custom_note = input.value;
        break;

      case 'delivery_date':
        this.selectedData.questions.delivery_date = input.value;
        break;
    }

  }

  updateDatePreview(dateInput) {
    const previewElement = this.container.querySelector('#step4-date-preview');
    if (!previewElement) return;

    const previewValue = previewElement.querySelector('.date-preview-value');
    if (!previewValue) return;

    // Update placeholder state for iOS
    this.updateDatePlaceholder(dateInput);

    // Update helper text visibility
    const helperText = dateInput.closest('.question-block')?.querySelector('.date-helper-text') ||
                       this.container.querySelector('#step4-date-helper');
    if (helperText) {
      if (dateInput.value) {
        helperText.classList.add('hidden');
      } else {
        helperText.classList.remove('hidden');
      }
    }

    if (dateInput.value) {
      const dateObj = new Date(dateInput.value);
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      previewValue.textContent = formattedDate;
      previewElement.classList.add('has-date');
    } else {
      previewValue.textContent = 'No date selected';
      previewElement.classList.remove('has-date');
    }
  }

  initializeDateHelperTexts() {
    // Initialize helper text visibility for all date inputs
    const dateInputs = this.container.querySelectorAll('.question-date');
    dateInputs.forEach(dateInput => {
      // Set placeholder text for iOS compatibility
      if (!dateInput.dataset.placeholder) {
        dateInput.dataset.placeholder = 'Select a date';
      }

      // Update placeholder state
      this.updateDatePlaceholder(dateInput);

      const helperText = dateInput.closest('.question-block')?.querySelector('.date-helper-text');
      if (helperText) {
        if (dateInput.value) {
          helperText.classList.add('hidden');
        } else {
          helperText.classList.remove('hidden');
        }
      }
    });
  }

  updateDatePlaceholder(dateInput) {
    // Add/remove empty-date class for placeholder display
    if (!dateInput.value || dateInput.value.trim() === '') {
      dateInput.classList.add('empty-date');
    } else {
      dateInput.classList.remove('empty-date');
    }
  }

  handleCustomQuestionChange(input) {
    const propertyName = input.dataset.propertyName;
    const questionType = input.dataset.questionType;

    if (!propertyName) return;

    if (questionType === 'multichoice') {
      // Handle multiple choice questions (now using select dropdown)
      if (input.tagName === 'SELECT') {
        this.selectedData.customQuestions[propertyName] = input.value || '';
      } else {
        // Fallback for old checkbox format (if any remain)
        const questionBlock = input.closest('.question-block');
        const checkedBoxes = questionBlock.querySelectorAll('.question-checkbox:checked');
        const values = Array.from(checkedBoxes).map(cb => cb.value);
        this.selectedData.customQuestions[propertyName] = values.join(', ');
      }
    } else {
      // Handle text, textarea, and date inputs
      this.selectedData.customQuestions[propertyName] = input.value;

      // Update helper text visibility for date inputs
      if (questionType === 'date') {
        const helperText = input.closest('.question-block')?.querySelector('.date-helper-text');
        if (helperText) {
          if (input.value) {
            helperText.classList.add('hidden');
          } else {
            helperText.classList.remove('hidden');
          }
        }
      }
    }

  }

  handleCategoryTextInputChange(input) {
    const categoryId = input.dataset.categoryId;
    const category = this.selectedData.categories.find(c => c.id === categoryId);

    if (category) {
      this.selectedData.categoryTextInputs[categoryId] = {
        categoryName: category.name,
        text: input.value
      };
    }
  }

  nextStep() {
    // Validate required fields before proceeding
    if (!this.canProceed()) {
      return;
    }

    let nextStepNumber = this.currentStep + 1;

    // Skip step 3 (subcategories) if surprise box is selected
    if (this.currentStep === 2 && this.selectedData.surpriseBox && !this.selectedData.categories.length) {
      nextStepNumber = 4; // Go directly to questions step
      console.log('Skipping to step 4 - Surprise box selected');
    }

    console.log('Moving from step', this.currentStep, 'to step', nextStepNumber);
    // Changed from totalSteps to 7 since we now have steps 0-6
    if (nextStepNumber <= 6) {
      this.goToStep(nextStepNumber);
    }
  }

  prevStep() {
    if (this.currentStep > 0) {
      let prevStepNumber = this.currentStep - 1;

      // Skip step 3 (subcategories) if surprise box is selected and no categories
      if (this.currentStep === 4 && this.selectedData.surpriseBox && !this.selectedData.categories.length) {
        prevStepNumber = 2; // Go back to category selection step
      } else if (this.currentStep === 5 && this.selectedData.surpriseBox && !this.selectedData.categories.length) {
        prevStepNumber = 2; // If on step 5 and surprise box, skip back to step 2
      }

      this.goToStep(prevStepNumber);
    }
  }

  goToStep(step) {
    const currentStepEl = this.container.querySelector(`.typeform-step[data-step="${this.currentStep}"]`);
    const nextStepEl = this.container.querySelector(`.typeform-step[data-step="${step}"]`);

    if (!nextStepEl) return;

    if (this.animationsEnabled) {
      // Smooth transition with animations
      currentStepEl.classList.remove('active');
      if (step > this.currentStep) {
        currentStepEl.classList.add('prev');
      } else {
        currentStepEl.classList.remove('prev');
      }

      nextStepEl.classList.add('active');
      nextStepEl.classList.remove('prev');
    } else {
      // Instant transition without animations
      currentStepEl.style.display = 'none';
      nextStepEl.style.display = 'flex';
      nextStepEl.classList.add('active');
    }

    this.currentStep = step;
    this.updateNavigationState();

    // Load subcategories when entering step 3
    if (step === 3) {
      this.loadSubcategories();
      this.restoreSubcategories();
      this.restoreCategoryTextInputs();
    }

    // Initialize date preview when entering step 5
    if (step === 5) {
      this.initDateInputs();
      const dateInput = this.container.querySelector('#step4-date');
      if (dateInput) {
        this.updateDatePreview(dateInput);
      }
      // Initialize helper text for all date inputs in step 5
      this.initializeDateHelperTexts();
    }

    // Load summary if on confirmation step (step 6)
    if (step === 6) {
      this.loadSummary();
      this.loadDeliveryInfo();
      this.loadAnswersDisplay();
    }

    // Save progress
    this.saveProgress();

    // Update progress indicator
    this.updateProgressIndicator();

    // Announce step change for screen readers
    this.announceStepChange();

    // Scroll to top of page when changing steps
    // Immediate scroll first to prevent any visual jump
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Then use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      // Small delay to ensure step transition is complete, then smooth scroll
      setTimeout(() => {
        // Scroll the next step element into view
        //if (nextStepEl) {
        //  nextStepEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        //}
        // Also ensure window is at top
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Scroll container if it's scrollable
        if (this.container && this.container.scrollTop > 0) {
          this.container.scrollTop = 0;
        }
      }, this.animationsEnabled ? 150 : 50);
    });
  }


  updateNavigationState() {
    const nextBtn = this.container.querySelector(`.typeform-step[data-step="${this.currentStep}"] [data-next-step]`);
    const addToCartBtn = this.container.querySelector('[data-add-to-cart]');

    if (nextBtn) {
      nextBtn.disabled = !this.canProceed();
    }

    if (addToCartBtn) {
      // Step 6 (summary) can always proceed
      if (this.currentStep === 6) {
        addToCartBtn.disabled = false;
      } else {
        addToCartBtn.disabled = !this.canProceed();
      }
    }

    // Add category validation message for step 2
    this.updateCategoryValidation();
  }

  updateCategoryValidation() {
    if (this.currentStep !== 2) return;

    const categoryContainer = this.container.querySelector('[data-category-selection]');
    if (!categoryContainer) return;

    // Remove existing validation message
    const existingMessage = categoryContainer.querySelector('.category-validation-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Only show validation if surprise box is not selected and categories are insufficient
    if (!this.selectedData.surpriseBox && this.selectedData.categories.length > 0 && this.selectedData.categories.length < 2) {
      const remaining = 2 - this.selectedData.categories.length;
      const message = document.createElement('div');
      message.className = 'category-validation-message';
      message.innerHTML = `
        <div class="validation-content">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.5"/>
            <path d="M10 6v4M10 14h0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span>Please select ${remaining} more ${remaining === 1 ? 'category' : 'categories'} to continue</span>
        </div>
      `;
      categoryContainer.appendChild(message);
    }
  }

  canProceed() {
    const canProceed = (() => {
      switch (this.currentStep) {
        case 0:
          return true; // Start page can always proceed
        case 1:
          return this.selectedData.product !== null;
        case 2:
          return this.selectedData.categories.length >= 2 || this.selectedData.surpriseBox;
        case 3:
          return true; // Subcategories are optional - customer can skip this step
        case 4:
          return this.validateRequiredFields(); // Check required custom questions
        case 5:
          return this.validateRequiredFields(); // Check required fields in step 5
        case 6:
          return true; // Final review can always proceed
        default:
          return false;
      }
    })();


    return canProceed;
  }

  validateRequiredFields() {
    const currentStepEl = this.container.querySelector(`.typeform-step[data-step="${this.currentStep}"]`);
    if (!currentStepEl) return true;

    const requiredFields = currentStepEl.querySelectorAll('[required]');

    for (const field of requiredFields) {
      let isValid = true;

      if (field.type === 'checkbox') {
        // For checkboxes, check if at least one in the group is checked
        const questionBlock = field.closest('.question-block');
        const checkboxes = questionBlock.querySelectorAll('input[type="checkbox"][required]');
        isValid = Array.from(checkboxes).some(cb => cb.checked);
      } else if (field.tagName === 'SELECT') {
        // For select dropdowns, check if value is not empty
        isValid = field.value !== '';
      } else if (field.type === 'date') {
        const min = field.getAttribute('min');
        isValid = field.value !== '' && (!min || field.value >= min);
      } else {
        // For text and textarea inputs, check if value is not empty
        isValid = field.value.trim() !== '';
      }

      if (!isValid) {
        return false;
      }
    }

    return true;
  }

  showFieldValidationErrors(invalidFields) {
    invalidFields.forEach(field => {
      const questionBlock = field.closest('.question-block');
      if (!questionBlock) return;

      // Add error class to the field
      field.classList.add('field-error');

      // Check if error message already exists
      let errorMessage = questionBlock.querySelector('.field-validation-error');

      if (!errorMessage) {
        // Create error message element
        errorMessage = document.createElement('div');
        errorMessage.className = 'field-validation-error';
        errorMessage.setAttribute('role', 'alert');
        errorMessage.setAttribute('aria-live', 'polite');

        // Insert after the input/select/textarea
        field.parentNode.insertBefore(errorMessage, field.nextSibling);
      }

      // Get field label for error message
      const label = questionBlock.querySelector('.question-label');
      const fieldName = label ? label.textContent.replace(/\s*\*$/, '').trim() : 'This field';

      errorMessage.textContent = `${fieldName} is required`;
      errorMessage.style.display = 'block';
    });

    // Show summary message at top of step
    this.showStepValidationSummary(invalidFields);
  }

  clearFieldValidationErrors(stepElement) {
    // Remove error classes from fields
    const errorFields = stepElement.querySelectorAll('.field-error');
    errorFields.forEach(field => field.classList.remove('field-error'));

    // Remove error messages
    const errorMessages = stepElement.querySelectorAll('.field-validation-error');
    errorMessages.forEach(msg => msg.remove());

    // Remove summary message
    const summaryMessage = stepElement.querySelector('.step-validation-summary');
    if (summaryMessage) {
      summaryMessage.remove();
    }
  }

  clearFieldError(field) {
    // Update navigation state to enable next button if all fields are valid
    this.updateNavigationState();
  }

  showStepValidationSummary(invalidFields) {
    const currentStepEl = this.container.querySelector(`.typeform-step[data-step="${this.currentStep}"]`);
    if (!currentStepEl) return;

    // Check if summary already exists
    let summaryMessage = currentStepEl.querySelector('.step-validation-summary');

    if (!summaryMessage) {
      summaryMessage = document.createElement('div');
      summaryMessage.className = 'step-validation-summary';
      summaryMessage.setAttribute('role', 'alert');

      // Insert at the top of step content, after step-header
      const stepContent = currentStepEl.querySelector('.typeform-content');
      const stepHeader = currentStepEl.querySelector('.step-header');
      if (stepContent && stepHeader) {
        stepContent.insertBefore(summaryMessage, stepHeader.nextSibling);
      } else if (stepContent) {
        stepContent.insertBefore(summaryMessage, stepContent.firstChild);
      }
    }

    const count = invalidFields.length;
    summaryMessage.innerHTML = `
      <div class="validation-content">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 6V10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 14H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Please fill in ${count} required field${count > 1 ? 's' : ''} before continuing.</span>
      </div>
    `;
    summaryMessage.style.display = 'block';
  }

  loadSummary() {
    const summaryContainer = this.container.querySelector('[data-selection-summary]');
    if (!summaryContainer) return;

    let summaryHTML = '';

    if (this.selectedData.product) {
      summaryHTML += `
        <div class="summary-item">
          <div class="summary-item-header">
            <div class="summary-label">Product</div>
            <div class="summary-edit" data-edit-step="1">Edit</div>
          </div>
          <div class="summary-value">${this.selectedData.product.name}</div>
        </div>
      `;
    }

    if (this.selectedData.categories.length > 0) {
      summaryHTML += `
        <div class="summary-item">
          <div class="summary-item-header">
            <div class="summary-label">Categories</div>
            <div class="summary-edit" data-edit-step="2">Edit</div>
          </div>
          <div class="summary-value">${this.selectedData.categories.map(c => c.name).join(', ')}</div>
        </div>
      `;
    }

    // Add surprise box to summary
    if (this.selectedData.surpriseBox) {
      summaryHTML += `
        <div class="summary-item">
          <div class="summary-item-header">
            <div class="summary-label">Surprise Box</div>
            <div class="summary-edit" data-edit-step="2">Edit</div>
          </div>
          <div class="summary-value">✨ Yes, make it a surprise!</div>
        </div>
      `;
    }

    if (this.selectedData.subcategories.length > 0) {
      summaryHTML += `
        <div class="summary-item">
          <div class="summary-item-header">
            <div class="summary-label">Subcategories</div>
            <div class="summary-edit" data-edit-step="3">Edit</div>
          </div>
          <div class="summary-value">${this.selectedData.subcategories.map(s => s.name).join(', ')}</div>
        </div>
      `;
    }

    // Add category-specific text inputs to summary
    Object.keys(this.selectedData.categoryTextInputs).forEach(categoryId => {
      const textInput = this.selectedData.categoryTextInputs[categoryId];
      if (textInput.text && textInput.text.trim() !== '') {
        summaryHTML += `
          <div class="summary-item">
            <div class="summary-item-header">
              <div class="summary-label">${textInput.categoryName} Details</div>
              <div class="summary-edit" data-edit-step="3">Edit</div>
            </div>
            <div class="summary-value">${textInput.text}</div>
          </div>
        `;
      }
    });

    summaryContainer.innerHTML = summaryHTML;
  }

  loadDeliveryInfo() {
    const deliveryContainer = this.container.querySelector('[data-delivery-summary]');
    if (!deliveryContainer) return;

    // Get the current values from the form inputs
    const textareaValue = this.container.querySelector('#step4-textarea')?.value || '';
    const dateValue = this.container.querySelector('#step4-date')?.value || '';

    let deliveryHTML = '';

    if (textareaValue) {
      deliveryHTML += `
        <div class="summary-delivery-item">
          <div class="summary-delivery-label">Your personal message:</div>
          <div class="summary-delivery-value">${textareaValue}</div>
        </div>
      `;
    }

    if (dateValue) {
      // Format the date nicely
      const dateObj = new Date(dateValue);
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      deliveryHTML += `
        <div class="summary-delivery-item">
          <div class="summary-delivery-label">Choose a date for your gift to arrive</div>
          <div class="summary-delivery-value">${formattedDate}</div>
        </div>
      `;
    } else {
      deliveryHTML += `
        <div class="summary-delivery-item">
          <div class="summary-delivery-label">Choose a date for your gift to arrive</div>
          <div class="summary-delivery-value">As soon as possible</div>
        </div>
      `;
    }

    deliveryContainer.innerHTML = deliveryHTML;
  }

  loadAnswersDisplay() {
    const answersContainer = this.container.querySelector('[data-answers-display]');
    if (!answersContainer) return;

    let answersHTML = '';

    // Add custom questions from blocks
    const processedQuestions = new Set();
    // Get all question blocks that contain custom questions (excluding the built-in textarea and date)
    const allQuestionBlocks = this.container.querySelectorAll('.question-block');

    allQuestionBlocks.forEach(questionBlock => {
      // Skip the built-in textarea and date questions
      if (questionBlock.querySelector('#step4-textarea') || questionBlock.querySelector('#step4-date')) {
        return;
      }
      // Find the first input/checkbox to get the property name
      const firstInput = questionBlock.querySelector('[data-property-name]');
      if (!firstInput) return;

      const propertyName = firstInput.dataset.propertyName;
      const questionType = firstInput.dataset.questionType;

      // Skip if we've already processed this question
      if (processedQuestions.has(propertyName)) return;
      processedQuestions.add(propertyName);

      const questionLabel = questionBlock.querySelector('.question-label')?.textContent?.replace(/\s*\*$/, '') || propertyName;

      let value = '';

      if (questionType === 'multichoice') {
        // Handle select dropdown for multichoice questions
        const select = questionBlock.querySelector(`select[data-property-name="${propertyName}"]`);
        if (select && select.value && select.value.trim() !== '') {
          value = select.value;
        } else {
          // Fallback for old checkbox format (if any remain)
          const checkedBoxes = questionBlock.querySelectorAll(`[data-property-name="${propertyName}"].question-checkbox:checked`);
          if (checkedBoxes && checkedBoxes.length > 0) {
            value = Array.from(checkedBoxes).map(cb => cb.value).join(', ');
          }
        }
      } else {
        const input = questionBlock.querySelector(`[data-property-name="${propertyName}"]`);
        if (input && input.value && input.value.trim() !== '') {
          if (questionType === 'date') {
            const dateObj = new Date(input.value);
            value = dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          } else {
            value = input.value;
          }
        }
      }

      if (value) {
        answersHTML += `
          <div class="summary-item">
            <div class="summary-item-header">
              <div class="summary-label">${questionLabel}</div>
            </div>
            <div class="summary-value">${value}</div>
          </div>
        `;
      }
    });

    answersContainer.innerHTML = answersHTML || '<p style="color: #666;">No additional information provided</p>';
  }

  announceStepChange() {
    const stepTitles = {
      0: 'Start Page',
      1: 'Product Selection',
      2: 'Category Selection',
      3: 'Subcategory Selection',
      4: 'Questions',
      5: 'Delivery Details',
      6: 'Review & Complete'
    };

    // Create a live region announcement
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = `Step ${this.currentStep}: ${stepTitles[this.currentStep]}`;

    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }

  addToCart() {
    if (!this.canProceed()) return;

    // Clear saved progress on successful submission
    sessionStorage.removeItem('giftBoxProgress');

    // Prepare line item properties
    const properties = {};

    if (this.selectedData.categories.length > 0) {
      properties['Gift Categories'] = this.selectedData.categories.map(c => c.name).join(', ');
    }

    if (this.selectedData.subcategories.length > 0) {
      properties['Gift Subcategories'] = this.selectedData.subcategories.map(s => s.name).join(', ');
    }

    // Add surprise box property
    if (this.selectedData.surpriseBox) {
      properties['Surprise Box'] = 'Yes';
    }

    // Add category-specific text inputs as properties
    Object.keys(this.selectedData.categoryTextInputs).forEach(categoryId => {
      const textInput = this.selectedData.categoryTextInputs[categoryId];
      if (textInput.text && textInput.text.trim() !== '') {
        properties[`${textInput.categoryName} Details`] = textInput.text;
      }
    });

    properties['_gift_form_completed'] = 'true';

    // Get variant ID from selected product
    let variantId;
    if (this.selectedData.product) {
      // If product was selected in step 1, use its variant ID
      const selectedCard = this.container.querySelector(`[data-product-id="${this.selectedData.product.id}"]`);
      variantId = selectedCard?.dataset.variantId || this.selectedData.product.variantId || this.selectedData.product.id;
    } else {
      // If no product selection step, get current page product variant
      variantId = this.getCurrentPageVariantId();
    }

    if (!variantId) {
      console.error('No product variant selected');
      this.showErrorMessage('Please select a product variant');
      return;
    }


    // Populate the form fields and submit the Shopify form
    this.populateFormAndSubmit(variantId, properties);
  }

  populateFormAndSubmit(variantId, properties) {
    const form = this.container.querySelector('#gift-product-form');
    if (!form) {
      console.error('Gift product form not found');
      return;
    }

    // Populate variant ID
    const variantInput = form.querySelector('[data-selected-variant-id]');
    if (variantInput) {
      variantInput.value = variantId;
    }

    // Populate line item properties (only if they have values)
    const categoryInput = form.querySelector('[data-gift-category]');
    if (categoryInput) {
      if (properties['Gift Categories']) {
        categoryInput.value = properties['Gift Categories'];
      } else {
        categoryInput.remove(); // Remove empty field
      }
    }

    const subcategoriesInput = form.querySelector('[data-gift-subcategories]');
    if (subcategoriesInput) {
      if (properties['Gift Subcategories']) {
        subcategoriesInput.value = properties['Gift Subcategories'];
      } else {
        subcategoriesInput.remove(); // Remove empty field
      }
    }

    // Populate question properties (only if they have values)
    const questionFields = {
      'occasion': form.querySelector('[data-gift-occasion]'),
      'allergies': form.querySelector('[data-gift-allergies]'),
      'color_palette': form.querySelector('[data-gift-color-palette]'),
      'quirky_answer': form.querySelector('[data-gift-quirky-answer]'),
      'recipient_count': form.querySelector('[data-gift-recipient-count]'),
      'surprise_box': form.querySelector('[data-gift-surprise-box]'),
      'custom_note': form.querySelector('[data-gift-custom-note]'),
      'delivery_date': form.querySelector('[data-gift-delivery-date]')
    };

    Object.keys(questionFields).forEach(key => {
      const input = questionFields[key];
      const value = this.selectedData.questions[key];

      if (!input) return;

      // Check if value is non-empty
      let hasValue = false;
      if (Array.isArray(value)) {
        hasValue = value.length > 0;
      } else if (value !== null && value !== undefined && value !== '') {
        if (typeof value === 'string') {
          hasValue = value.trim() !== '';
        } else {
          hasValue = true; // Non-string values are considered valid
        }
      }

      if (hasValue) {
        if (Array.isArray(value)) {
          input.value = value.join(', ');
        } else {
          input.value = value;
        }
      } else {
        // Remove empty field from form
        input.remove();
      }
    });

    // Add custom questions as line item properties
    Object.keys(this.selectedData.customQuestions).forEach(propertyName => {
      const value = this.selectedData.customQuestions[propertyName];
      if (value && value.trim() !== '') {
        // Create a hidden input for each custom question
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = `properties[${propertyName}]`;
        hiddenInput.value = value;
        form.appendChild(hiddenInput);
      }
    });


    // Submit the form
    this.showSuccessMessage();
    setTimeout(() => {
      form.submit();
    }, 500);
  }

  getCurrentPageVariantId() {
    // Try to get variant ID from the current product page
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect) {
      return variantSelect.value;
    }

    // Try to get from product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('[name="id"]');
      if (variantInput) {
        return variantInput.value;
      }
    }

    // Try to get from URL or window object
    if (window.meta && window.meta.product && window.meta.product.variants) {
      return window.meta.product.variants[0].id;
    }

    return null;
  }

  performAddToCart(variantId, properties) {
    const formData = {
      items: [{
        id: variantId,
        quantity: 1,
        properties: properties
      }]
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      this.showSuccessMessage();

      // Trigger cart drawer or redirect if needed
      if (window.cart && typeof window.cart.refresh === 'function') {
        window.cart.refresh();
      }

      // Dispatch custom event for cart updates
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { items: data.items || [data], properties }
      }));
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      this.showErrorMessage('Failed to add to cart. Please try again.');
    });
  }

  getCurrentProductId() {
    // In a real implementation, this would get the current product ID from the page
    return window.location.pathname.includes('/products/') ? 'current-product' : null;
  }

  // Helper functions for product data
  stripHtml(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  formatPrice(price) {
    // Handle different price formats from Shopify API
    let numPrice = parseFloat(price);

    // Shopify API returns prices as strings like "29.99" (already in dollars)
    // Only divide by 100 if the price seems to be in cents (very large number)
    if (numPrice > 999) {
      numPrice = numPrice / 100;
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numPrice);
  }

  showSuccessMessage() {
    const btn = this.container.querySelector('[data-add-to-cart]');
    const originalText = btn.textContent;

    btn.textContent = 'Added to Cart!';
    btn.disabled = true;
    btn.classList.add('success');

    setTimeout(() => {
      btn.textContent = originalText;
      btn.disabled = false;
      btn.classList.remove('success');
    }, 2000);
  }

  showErrorMessage(message) {
    const btn = this.container.querySelector('[data-add-to-cart]');
    const originalText = btn.textContent;

    btn.textContent = message;
    btn.disabled = true;
    btn.classList.add('error');

    setTimeout(() => {
      btn.textContent = originalText;
      btn.disabled = false;
      btn.classList.remove('error');
    }, 3000);
  }

  updateProgressIndicator() {
    const progressIndicator = this.container.querySelector('[data-progress-indicator]');
    if (!progressIndicator) return;

    // Hide progress indicator on step 0 (start page)
    if (this.currentStep === 0) {
      progressIndicator.style.display = 'none';
      return;
    }

    // Show progress indicator for steps 1-6
    progressIndicator.style.display = 'block';

    // Update active step (steps 1-6 map to progress steps 1-6)
    const progressSteps = progressIndicator.querySelectorAll('[data-progress-step]');
    progressSteps.forEach((stepEl, index) => {
      const stepNumber = parseInt(stepEl.dataset.progressStep);
      // Step 1 in form = progress step 1, Step 2 = progress step 2, etc.
      if (this.currentStep === stepNumber) {
        stepEl.setAttribute('data-active', '');
      } else {
        stepEl.removeAttribute('data-active');
      }
    });
  }

}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const giftFormContainers = document.querySelectorAll('[data-gift-form]');
  giftFormContainers.forEach(container => {
    new GiftForm(container);
  });
});
